import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Play, Film, Video, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { serviceSchema, faqSchema as toFaqSchema } from "@/data/schema";
import CtaBanner from "@/components/CtaBanner";

export default function EcommerceVideoCreation() {
  const schema = serviceSchema({
    name: "E-commerce Product Video Creation Services",
    description: "E-commerce Product Video Creation Services from Orina Enterprise for marketplace and D2C brands.",
    path: "/services/ecommerce-video-creation",
    serviceType: "E-commerce product video production",
  });
  const faqs = [
    { q: "How long is a typical marketplace product video?", a: "30-45 seconds for Amazon and Flipkart listing videos. 15-30 seconds for Reels and social ads." },
    { q: "Do you shoot videos in-house or partner?", a: "Both — a partner studio network in Mumbai, Delhi and Bangalore for shoots, in-house edit and motion." },
    { q: "How long does a video production cycle take?", a: "Concept to delivered final: typically 2-3 weeks per SKU family." },
    { q: "Do you produce UGC-style content?", a: "Yes. We use a creator network for UGC-style product demonstrations." }
  ];
  const faqSchema = toFaqSchema(faqs);

  return (
    <>
      <SEO
        title="E-commerce Product Video Creation Services India"
        description="Product videos, marketplace videos, reels, ads and festive campaign creatives for Indian e-commerce brands. Full-service production and edit."
        keywords="ecommerce product video creation, product videos India, marketplace video, Reels for ecommerce, Amazon product video"
        path="/services/ecommerce-video-creation"
        schema={[schema, faqSchema]}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "E-commerce Video Creation", path: "/services/ecommerce-video-creation" }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Creative Services</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            Product Videos & Marketing Creatives for E-commerce Brands
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">
            Marketplace product videos, short-form ads, Reels, social videos, demonstrations, UGC concepts, motion graphics and festive campaign creatives — for Indian D2C brands.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact/?type=expert" className="btn-brutal">Talk to a video expert <ArrowUpRight size={16} /></Link>
            <Link to="/services/a-plus-listing-graphics/" className="btn-brutal secondary">See A+ graphics too</Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="eyebrow">Why product videos matter</div>
          <h2 className="display mt-3 text-3xl font-black md:text-4xl">Video is the highest-leverage creative on a listing</h2>
          <p className="mt-4 text-base leading-relaxed text-black/85 md:text-lg">
            Amazon reports that listing videos meaningfully lift conversion for many product categories. On Flipkart and Meesho, videos drive scroll-stop and reduce return rate by setting accurate expectations. On social and quick-commerce ads, short-form video is now the dominant creative format.
          </p>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">Types of videos we produce</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { i: <Play size={22} />, t: "Marketplace product videos", d: "30-45s videos designed for the Amazon and Flipkart buy-box. Product-on-white opener + benefit sequence + brand close." },
              { i: <Video size={22} />, t: "Short-form ads & Reels", d: "15-30s creatives for Meta, Instagram Reels and YouTube Shorts." },
              { i: <Film size={22} />, t: "UGC-style demos", d: "Real people using your product, shot with a creator network." },
              { i: <Sparkles size={22} />, t: "Motion graphics", d: "Animated explainers, ingredient reveals, comparison visuals." },
              { i: <Play size={22} />, t: "Festive campaign creatives", d: "Sale event videos for Big Billion Days, Great Indian Festival, Pink Friday." },
              { i: <Film size={22} />, t: "Brand story films", d: "1-2 minute films for D2C websites, retail decks, PR outreach." }
            ].map((s) => (
              <div key={s.t} className="card-brutal">
                <div className="flex h-11 w-11 items-center justify-center border-2 border-black bg-black text-[#FFD700]">{s.i}</div>
                <h3 className="display mt-4 text-lg font-black">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/75">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="eyebrow">Production workflow</div>
          <h2 className="display mt-3 text-3xl font-black md:text-4xl">From brief to shipping</h2>
          <ol className="mt-8 space-y-4">
            {[
              "Discovery: brief, audience, marketplace formats, references",
              "Concept & storyboard: shot-by-shot planning",
              "Pre-production: cast, location, props, product prep",
              "Shoot: partner studio in Mumbai / Delhi / Bangalore",
              "Edit: assembly, colour, sound, captions",
              "Motion graphics: text overlays, comparison animations",
              "Delivery: platform-specific aspect ratios (1:1, 4:5, 9:16, 16:9)"
            ].map((s, i) => (
              <li key={s} className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center border-2 border-black bg-[#FFD700] text-sm font-black">{i + 1}</div>
                <span className="pt-1 text-base md:text-lg">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">FAQ</h2>
          <div className="mt-8 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="card-brutal">
                <summary className="cursor-pointer text-base font-bold md:text-lg">{f.q}</summary>
                <p className="mt-3 text-sm text-black/80 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title="Need product videos that convert?" subtitle="Share your product and target platforms — we'll come back with concept + budget in 48 hours." />
    </>
  );
}
